/** @file applyDragToKey: a gizmo gesture folded into the node key it started from (pure). */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import { quatFromYawPitchRoll } from '@zilo/pose3d-math/quat/quatFromYawPitchRoll';
import { quatMul } from '@zilo/pose3d-math/quat/quatMul';
import type { TransformSpace } from '../../state/editorTypes';
import { axisAngleQuat } from './axisAngleQuat';
import type { KeyDrag } from './viewportDrag';
import { yawPitchRollOf } from './yawPitchRollOf';

/** Rounds to `step` (keeps documents free of float noise). */
const round = (v: number, step: number): number => Math.round(v / step) * step + 0;

/** `deg` rounded to a multiple of `snapDeg` (0 = no snap). */
const snapped = (deg: number, snapDeg: number): number => (snapDeg > 0 ? Math.round(deg / snapDeg) * snapDeg : deg);

/**
 * `rotate`: the start rotation turned by the (snapped) delta about `axis`, after it (`local`, axis in the node's frame)
 * or before it (`world`, axis in the parent's frame), read back as yaw/pitch/roll nearest the start, each wrapped
 * into (-180, 180] (a drag across the yaw seam stays the short way). `move`: `pos + delta`. `scale`: `scale x ratio`
 * (at least 0.01; snapped to 0.1 while `snapDeg > 0`). Other fields of the start key are kept.
 */
export function applyDragToKey(startKey: NodeKey, drag: KeyDrag, space: TransformSpace, snapDeg: number): NodeKey {
  switch (drag.kind) {
    case 'rotate': {
      const start = startKey.rot ?? [0, 0, 0];
      const turn = axisAngleQuat(drag.axis, snapped(drag.deltaDeg, snapDeg));
      const q0 = quatFromYawPitchRoll(start[0], start[1], start[2]);
      const ypr = yawPitchRollOf(space === 'local' ? quatMul(q0, turn) : quatMul(turn, q0), start);
      return { ...startKey, rot: [round(ypr[0], 1e-3), round(ypr[1], 1e-3), round(ypr[2], 1e-3)] };
    }
    case 'move': {
      const p = startKey.pos ?? [0, 0, 0];
      return { ...startKey, pos: [round(p[0] + drag.delta[0], 1e-4), round(p[1] + drag.delta[1], 1e-4), round(p[2] + drag.delta[2], 1e-4)] };
    }
    case 'scale': {
      const s = Math.max(0.01, (startKey.scale ?? 1) * drag.ratio);
      return { ...startKey, scale: snapDeg > 0 ? Math.max(0.1, round(s, 0.1)) : round(s, 1e-3) };
    }
  }
}
